import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useVerificationPhotos } from '../../../hooks/useVerificationPhotos';
import { VerificationPhotoThumbnails } from '../../product-verification/VerificationPhotoThumbnails';
import { VerificationPhotoLightbox } from '../../product-verification/VerificationPhotoLightbox';
import type { VerificationPhoto } from '../../../services/verificationPhotoService';

export interface OrderVerificationPhotosRowProps {
  pharmacyId: string;
  orderId: string;
  orderNumber?: string | null;
  /**
   * Optional render-prop so the drugs table rendered in the same expanded row
   * can badge each Rx's Product check with the same photo list this component
   * already fetched, instead of issuing a second query for the same order.
   */
  children?: (photos: VerificationPhoto[]) => React.ReactNode;
}

/**
 * PRX-68 — product-verification photos for one order row on the Patient 360
 * Orders tab. Mounted only while that row's `Collapse` is open (its parent
 * uses `unmountOnExit`), so the photo query naturally fires only for an
 * EXPANDED order row (R5 of the verification-photo-everywhere plan) — no
 * separate `enabled` flag needed. Mirrors `OrderVerificationPhotosSection`'s
 * strip + lightbox; renders nothing when the order has no photos.
 */
export const OrderVerificationPhotosRow: React.FC<OrderVerificationPhotosRowProps> = ({
  pharmacyId,
  orderId,
  orderNumber,
  children,
}) => {
  const { data } = useVerificationPhotos(pharmacyId, orderId);
  const photos = Array.isArray(data) ? data : [];
  const [openPhoto, setOpenPhoto] = useState<VerificationPhoto | null>(null);

  return (
    <>
      {children?.(photos)}
      {photos.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Product verification photos
          </Typography>
          <VerificationPhotoThumbnails
            pharmacyId={pharmacyId}
            orderId={orderId}
            photos={photos}
            onOpen={(photo) => setOpenPhoto(photo)}
          />
          <VerificationPhotoLightbox
            open={openPhoto !== null}
            onClose={() => setOpenPhoto(null)}
            pharmacyId={pharmacyId}
            orderId={orderId}
            photo={openPhoto}
            orderNumber={orderNumber}
          />
        </Box>
      )}
    </>
  );
};

export default OrderVerificationPhotosRow;
